import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, Progress, Tooltip, Skeleton } from "antd";
import ReactHighcharts from "react-highcharts";
import { formatAmount } from "../../constants/overview";
import { RoundedSelect } from "../tailwind/atoms";
import OpdIcon from "./OpdIcon";
import { DownloadPngButton } from "./primitives";
import { DS_CHART_COLORS, DS_CHART_TOKENS, normalizeHighchartsConfig } from "../../utils/chartTheme";

const relationshipColors = {
  self: DS_CHART_TOKENS.semBlue400,
  spouse: DS_CHART_TOKENS.amber400,
  children: DS_CHART_TOKENS.green400,
  child: DS_CHART_TOKENS.green400,
  parents: DS_CHART_TOKENS.rose400,
  mother: DS_CHART_TOKENS.rose400,
  father: DS_CHART_TOKENS.purple400,
  sibling: DS_CHART_TOKENS.gray700,
  mother_in_law: DS_CHART_TOKENS.green300,
  father_in_law: DS_CHART_TOKENS.gray500,
  grand_mother: DS_CHART_TOKENS.amber300,
  grand_father: DS_CHART_TOKENS.green300,
  other: DS_CHART_TOKENS.semBlue300,
  unknown: DS_CHART_TOKENS.gray300
};

const defaultColors = DS_CHART_COLORS;

const MAX_VISIBLE_ITEMS = 8;

const RelationshipSplit = ({ data, loading = false, servicesMap = {}, currentService, handleServiceChange = () => {} }) => {
  const [metric, setMetric] = useState("amount");
  const [donutOptions, setDonutOptions] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [listMaxHeight, setListMaxHeight] = useState("0px");
  const listWrapperRef = useRef(null);
  const cardRef = useRef(null);

  const numberFormatter = new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0
  });

  useEffect(() => {
    if (!data) return;
    const categories = Object.keys(data);
    const total = categories.reduce((sum, cat) => sum + data[cat][metric], 0);

    const chartData = categories.map((cat) => ({
      name: cat.charAt(0).toUpperCase() + cat.slice(1),
      y: data[cat][metric],
      color:
        relationshipColors[cat.split("/")[0].toLowerCase().replace(/[\s-]+/g, "_")] ||
        defaultColors[categories.indexOf(cat) % defaultColors.length],
      percentage: total
        ? parseFloat(((data[cat][metric] / total) * 100).toFixed(1))
        : 0
    }));

    setDonutOptions({
      chart: { type: "pie" },
      title: { text: "" },
      credits: { enabled: false },
      tooltip: {
        pointFormatter: function () {
          const val =
            metric === "amount"
              ? `₹${numberFormatter.format(this.y)}`
              : numberFormatter.format(this.y);
          return `<b>${this.name}:</b> ${val} (${this.percentage.toFixed(1)}%)`;
        }
      },
      plotOptions: {
        pie: {
          innerSize: "60%",
          dataLabels: {
            enabled: true,
            formatter: function () {
              const val =
                metric === "amount"
                  ? `₹${formatAmount(this.y)}`
                  : numberFormatter.format(this.y);
              return `${this.point.name}: ${val}`;
            },
            style: { fontSize: "11px", fontFamily: "JetBrains Mono", color: DS_CHART_TOKENS.gray700 }
          }
        }
      },
      series: [{ name: metric, data: chartData }]
    });
  }, [metric, data]);

  useEffect(() => {
    const wrapper = listWrapperRef.current;
    if (!wrapper) return;

    if (expanded) {
      const fullHeight = wrapper.scrollHeight;
      setListMaxHeight(`${fullHeight}px`);
      const t = setTimeout(() => setListMaxHeight("none"), 350);
      return () => clearTimeout(t);
    }

    const firstChild = wrapper.querySelector(".relation-row");
    const rowHeight = firstChild ? firstChild.getBoundingClientRect().height : 56;
    const collapseHeight = Math.ceil(rowHeight * MAX_VISIBLE_ITEMS);
    setListMaxHeight(`${collapseHeight}px`);
  }, [expanded, data]);

  if (loading) {
    return (
      <Card
        className="border border-gray-300 shadow-sm"
        style={{ borderRadius: 14, overflow: "hidden" }}
        bodyStyle={{ borderRadius: 14 }}
      >
        <h1 className="mb-8 flex items-center gap-4 text-base leading-none text-gray-900">
          <OpdIcon name="relationship" className="h-6 w-6 rounded border border-blue-200 bg-blue-100 p-1.5 text-blue-700" />
          <span className="font-fig-semibold">Relationship-wise Split</span>
        </h1>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }

  if (!data) {
    return (
      <Card
        className="border border-gray-300 shadow-sm"
        title="Relationship-wise Split"
        style={{ borderRadius: 14, overflow: "hidden" }}
        bodyStyle={{ borderRadius: 14 }}
      >
        <p>No data available</p>
      </Card>
    );
  }

  const categories = Object.keys(data);
  const totalCount = categories.reduce((sum, cat) => sum + data[cat].volume, 0);
  const totalAmount = categories.reduce((sum, cat) => sum + data[cat].amount, 0);

  return (
    <div ref={cardRef}>
    <Card
      className="border border-gray-300 shadow-sm"
      style={{ borderRadius: 14, overflow: "hidden" }}
      bodyStyle={{ borderRadius: 14 }}
    >
      <div className="flex justify-between items-center mb-6">
        <h1 className="mb-8 flex items-center gap-4 text-base leading-none text-gray-900">
          <OpdIcon name="relationship" className="h-6 w-6 rounded border border-blue-200 bg-blue-100 p-1.5 text-blue-700" />
          <span className="font-fig-semibold">Relationship-wise Split</span>
          <DownloadPngButton nodeRef={cardRef} fileName="relationship-split" />
        </h1>

        <div className="mb-4 inline-flex space-x-1 overflow-x-auto rounded-md border border-gray-300 bg-gray-200 p-1">
          {["volume", "amount"].map((type) => {
            const isActive = metric === type;
            return (
              <button
                key={type}
                onClick={() => setMetric(type)}
                className={`rounded-sm px-3 py-1.5 text-sm font-fig-semibold transition-colors duration-200 focus:outline-none ${
                  isActive
                    ? "rounded-md bg-white text-gray-900 shadow-sm"
                    : "text-gray-600"
                }`}
              >
                {type === "volume" ? "Count" : "Value (₹)"}
              </button>
            );
          })}
        </div>

        <RoundedSelect
          listClasses="w-34"
          classes="ml-8 -mt-2"
          selectOptions={Object.values(servicesMap)}
          selectValues={Object.keys(servicesMap).map((_, i) => i)}
          curvalue={servicesMap[currentService] || 'Overall'}
          selectHandler={(index) =>
            handleServiceChange('relationship_split', Object.keys(servicesMap)[index])
          }
        />
      </div>

      <Row gutter={16}>
        {/* Donut Chart */}
        <Col span={12}>
          {donutOptions && <ReactHighcharts config={normalizeHighchartsConfig(donutOptions)} />}
        </Col>

        <Col span={12}>
          {/* Header Row */}
          <div className="flex justify-between items-center mb-4 px-2 sm:px-4">
            <div className="w-1/4" />
            <div className="w-2/4" />
            <div className="flex justify-end w-1/4 gap-6 text-xs sm:text-sm font-fig-normal tracking-wide text-gray-600">
              <span>Share</span>
              <span>AOV</span>
            </div>
          </div>

          {/* Scroll Wrapper */}
          <div
            ref={listWrapperRef}
            style={{
              maxHeight: listMaxHeight === "none" ? "none" : listMaxHeight,
              overflow: "hidden",
              transition: "max-height 320ms ease, opacity 200ms ease"
            }}
          >
            {categories.map((cat, idx) => {
              const value = data[cat][metric];
              const aov = data[cat].amount / (data[cat].volume || 1);
              const percent =
                metric === "volume"
                  ? parseFloat(((value / totalCount) * 100).toFixed(1))
                  : parseFloat(((value / totalAmount) * 100).toFixed(1));
              const color =
                relationshipColors[cat.split("/")[0].toLowerCase().replace(/[\s-]+/g, "_")] ||
                defaultColors[idx % defaultColors.length];

              return (
                <div
                  key={cat}
                  className="relation-row my-1 flex flex-col items-center justify-between gap-3 rounded-lg p-2 transition-all hover:bg-gray-50 sm:flex-row sm:gap-4 sm:p-3"
                >
                  {/* Category + Dot */}
                  <div className="flex items-center gap-3 w-full sm:w-1/4">
                    <div
                      className="w-3 h-3 rounded-full shadow-sm flex-shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    <Tooltip title={cat.charAt(0).toUpperCase() + cat.slice(1)}>
                      <span className="truncate text-sm font-fig-normal text-gray-900">
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </span>
                    </Tooltip>
                  </div>

                  {/* Progress Bar */}
                  <Tooltip
                    title={`${cat.charAt(0).toUpperCase() + cat.slice(1)}: ${
                      metric === "amount"
                        ? `₹${numberFormatter.format(value)}`
                        : numberFormatter.format(value)
                    } (${percent}%)`}
                    placement="topLeft"
                  >
                    <Progress
                      percent={percent}
                      showInfo={false}
                      strokeColor={color}
                      trailColor={DS_CHART_TOKENS.gray200}
                      strokeWidth={8}
                      className="w-full sm:w-2/4 cursor-pointer"
                    />
                  </Tooltip>

                  {/* Share + AOV */}
                  <div className="flex w-full justify-end gap-6 text-sm font-jetbrains-mono font-fig-semibold tabular-nums text-gray-900 sm:w-1/4 sm:text-base">
                    <span className="w-14 text-right" style={{ color: DS_CHART_TOKENS.semBlue400 }}>
                      {percent}%
                    </span>
                    <span className="w-20 text-right text-green-700">
                      ₹{numberFormatter.format(aov)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Expand/Collapse Button */}
          {categories.length > MAX_VISIBLE_ITEMS && (
            <div className="flex justify-center mt-4">
              <button
                onClick={() => setExpanded((s) => !s)}
                className="flex items-center gap-2 rounded-full border border-gray-300 px-5 py-2 text-sm font-fig-semibold text-gray-700 shadow-sm transition-all duration-200 hover:bg-gray-100"
                aria-expanded={expanded}
              >
                <span>
                  {expanded
                    ? "Show Less"
                    : `Show More (${categories.length - MAX_VISIBLE_ITEMS})`}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 transform transition-transform duration-200 ${
                    expanded ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Totals Card */}
          <div className="mt-4 rounded-lg border border-gray-300 bg-gray-50 p-4 shadow-sm">
            <div className="flex flex-col sm:flex-row items-center justify-between text-sm sm:text-base gap-6 sm:gap-0">
              <div className="flex flex-col items-center sm:items-start">
                <span className="font-fig-normal text-gray-600">Total Claims</span>
                <span className="font-jetbrains-mono text-base font-fig-semibold tabular-nums sm:text-lg" style={{ color: DS_CHART_TOKENS.semBlue400 }}>
                  {numberFormatter.format(totalCount)}
                </span>
              </div>
              <div className="mx-4 hidden h-10 w-px bg-gray-300 sm:block" />
              <div className="flex flex-col items-center sm:items-end">
                <span className="font-fig-normal text-gray-600">Total Value</span>
                <span className="font-jetbrains-mono text-base font-fig-semibold tabular-nums text-green-700 sm:text-lg">
                  ₹{numberFormatter.format(totalAmount)}
                </span>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Card>
    </div>
  );
};

export default RelationshipSplit;
